import { axiosJSONInstance, axiosMultipartInstance } from '@/config';
import { TicketFilters } from '@/stores';
import { ISearchTicket, TicketSortKeyEnum } from '@/stores/useTicketingStore';
import {
  TicketFormResponse,
  ResponseType,
  TicketList,
  SingleTicketResponse,
  TicketCommentResponse,
  MessageTokenResponse,
} from '@/types';
import { createPaginationSearchParams, getFilterTicketQuery, getSortByParams } from '@/utils';

// Get zd messaging token
export const getMessagingToken = async (): Promise<ResponseType<MessageTokenResponse>> => {
  try {
    const response = await axiosJSONInstance.get(`/merchants/tickets/messaging/token`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Tickets:', error);
    throw error;
  }
};
// Fetch all Tickets Fields
export const fetchFormFields = async (): Promise<ResponseType<TicketFormResponse[]>> => {
  try {
    const response = await axiosJSONInstance.get(`/merchants/tickets/forms`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Tickets:', error);
    throw error;
  }
};

// Fetch all Tickets
export const fetchTickets = async (
  page: number,
  pageSize?: number,
  sortBy?: TicketSortKeyEnum,
): Promise<ResponseType<TicketList>> => {
  const params = createPaginationSearchParams(page, pageSize);

  if (sortBy) {
    getSortByParams(params, sortBy);
  }
  try {
    const response = await axiosJSONInstance.get(`/merchants/tickets?${params}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Tickets:', error);
    throw error;
  }
};

// Fetch all Tickets
export const filterTickets = async (
  page: number,
  pageSize?: number,
  ticketFilters?: TicketFilters,
  platformCode?: string,
  sortBy?: TicketSortKeyEnum,
  customId?: string,
): Promise<ResponseType<TicketList>> => {
  const paginateParams = createPaginationSearchParams(page, pageSize);
  const filterParams = getFilterTicketQuery(
    ticketFilters,
    platformCode,
    undefined,
    sortBy,
    customId,
  );

  const params = `?${paginateParams.toString() + (filterParams.toString() ? '&' + filterParams.toString() : '')}`;

  try {
    const response = await axiosJSONInstance.get(`/merchants/tickets/search${params}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Tickets:', error);
    throw error;
  }
};
// Fetch Tickets by ids
export const fetchTicketsByIDs = async (
  ticketIds: string[],
): Promise<ResponseType<SingleTicketResponse[]>> => {
  const filterParams = getFilterTicketQuery(undefined, undefined, ticketIds);

  const params = `?${filterParams.toString() ? '&' + filterParams.toString() : ''}`;

  try {
    const response = await axiosJSONInstance.get(`/merchants/tickets/ids${params}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Tickets:', error);
    throw error;
  }
};

// Fetch  Ticket
export const fetchTicketById = async (id: string): Promise<ResponseType<SingleTicketResponse>> => {
  try {
    const response = await axiosJSONInstance.get(`/merchants/tickets/${id}`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Ticket:', error);
    throw error;
  }
};

// Update an ticket by ID
export const updateTicket = async (ticket: Partial<SingleTicketResponse>): Promise<any> => {
  const response = await axiosJSONInstance.patch(`/merchants/tickets/${ticket.id}`, ticket);
  return response.data;
};

// Fetch Ticket comments
export const fetchTicketComments = async (
  id: string,
): Promise<ResponseType<TicketCommentResponse>> => {
  try {
    const response = await axiosJSONInstance.get(`/merchants/tickets/${id}/comments`, {
      params: {
        //TODO: without test
        page: '1',
        perpage: 200,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch Ticket:', error);
    throw error;
  }
};

// Create a new Ticket
export const createTicket = async (Ticket: FormData): Promise<any> => {
  const response = await axiosMultipartInstance.post('/merchants/tickets', Ticket);
  return response.data;
};

// Create a new Comment On Ticket
export const createCommentOnTicket = async (
  comment: FormData,
  ticketId: string,
): Promise<FormData> => {
  const response = await axiosMultipartInstance.post(
    `/merchants/tickets/${ticketId}/comments`,
    comment,
  );
  return response.data;
};

// Fetch ticket counts for the dashboard Overview card and the ticket-center
// status tabs. Backend now hits the upstream /tickets/stats aggregate (O(1))
// and exposes both legacy `counts` and the new `by_status`/`summary` fields.
export const fetchPendingTicketCount = async (params?: {
  from_date?: string;
  to_date?: string;
}): Promise<{
  status: string;
  object: {
    counts: Record<string, number>;
    by_status: Record<string, number>;
    total: number;
    total_pending: number;
    summary: { active: number; completed: number };
    new?: number;
    open?: number;
  };
}> => {
  const response = await axiosJSONInstance.get('/merchants/tickets/counts', {
    params,
  });
  return response.data;
};
